// Shared InstantSearch/Algolia grid logic for main-collection.liquid and
// search-results.liquid. Both sections render product hits client-side from
// Algolia JSON (not Liquid's product-card.liquid, which only runs server-side),
// so this is the one place their hit markup, tag badges, and generic widgets
// (refinement lists, price menu, pagination, reset button, mobile filter
// drawer) live — keep it here instead of copy-pasting into each section.
window.BlueRoseAlgoliaGrid = (function () {
  'use strict';

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatPrice(price) {
    if (price == null || price === '') return '';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(price));
  }

  // ── Tag badges — mirrors snippets/product-tags.liquid ────────────────────

  function tagBadgesHtml(hit) {
    var badges = (hit.tags || []).map(function (tag) {
      var t = String(tag).toLowerCase();
      if (t.indexOf('sale') !== -1) {
        return '<span class="bg-red-600 text-white text-[10px] px-2 py-[2px] rounded-sm uppercase tracking-wide">Sale</span>';
      }
      if (t.indexOf('clearance') !== -1) {
        return '<span class="bg-yellow-500 text-white text-[10px] px-2 py-[2px] rounded-sm uppercase tracking-wide">' + esc(tag) + '</span>';
      }
      if (t.indexOf('new arrival') !== -1) {
        return '<span class="bg-green-600 text-white text-[10px] px-2 py-[2px] rounded-sm uppercase tracking-wide">' + esc(tag) + '</span>';
      }
      if (t.indexOf('best seller') !== -1 || t.indexOf('bestseller') !== -1) {
        return '<span class="bg-[#00538d] text-white text-[10px] px-2 py-[2px] rounded-sm uppercase tracking-wide">Bestseller</span>';
      }
      if (t.indexOf('back in stock') !== -1) {
        return '<span class="bg-green-400 text-white text-[10px] px-2 py-[2px] rounded-sm uppercase tracking-wide">Back in Stock</span>';
      }
      return '';
    }).filter(Boolean);

    if (!badges.length) return '';
    return '<div class="absolute top-2 left-2 flex flex-wrap gap-1 z-20">' + badges.join('') + '</div>';
  }

  // Collection handle/title helpers and the Category/Pattern handle sets live in
  // algolia-collection-sets.js (shared with the mobile menu) — loaded before this file.
  var collectionSets       = window.BlueRoseCollectionSets;
  var toHandle             = collectionSets.toHandle;
  var collectionLabel      = collectionSets.collectionLabel;
  var CATEGORY_HANDLES     = collectionSets.CATEGORY_HANDLES;
  var PATTERN_ONLY_EXCLUDE = collectionSets.PATTERN_ONLY_EXCLUDE;

  // Swap labels already on the page once titles arrive — avoids re-running the search
  function applyCollectionTitles() {
    document.querySelectorAll('[data-collection-label]').forEach(function (el) {
      el.textContent = collectionLabel(el.getAttribute('data-collection-label'));
    });
  }

  function loadCollectionTitles() {
    collectionSets.loadCollectionTitles().then(function (map) {
      if (map) applyCollectionTitles();
    });
  }

  // ── Wishlist toggle — mirrors snippets/wishlist-toggle.liquid ────────────
  // Click handling and saved-state sync are delegated globally by
  // snippets/wishlist-init.liquid (window.BlueRoseWishlist), so this only
  // needs to render matching markup; syncWishlistButtons() is called after
  // each hits render to pick up the current saved state.

  function wishlistButtonHtml(hit) {
    var productId = esc(hit.id || '');
    return (
      '<div data-product-card-wishlist>' +
        '<button' +
          ' type="button"' +
          ' class="absolute top-3 right-3 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[#00538d] shadow cursor-pointer opacity-90"' +
          ' data-wishlist-toggle' +
          ' data-wishlist-product-id="' + productId + '"' +
          ' aria-label="Add to wishlist"' +
          ' aria-pressed="false"' +
        '>' +
          '<span data-wishlist-icon-off class="inline-flex h-6 w-6 items-center justify-center">' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="h-6 w-6 fill-current"><path d="M442.9 144C415.6 144 389.9 157.1 373.9 179.2L339.5 226.8C335 233 327.8 236.7 320.1 236.7C312.4 236.7 305.2 233 300.7 226.8L266.3 179.2C250.3 157.1 224.6 144 197.3 144C150.3 144 112.2 182.1 112.2 229.1C112.2 279 144.2 327.5 180.3 371.4C221.4 421.4 271.7 465.4 306.2 491.7C309.4 494.1 314.1 495.9 320.2 495.9C326.3 495.9 331 494.1 334.2 491.7C368.7 465.4 419 421.3 460.1 371.4C496.3 327.5 528.2 279 528.2 229.1C528.2 182.1 490.1 144 443.1 144zM335 151.1C360 116.5 400.2 96 442.9 96C516.4 96 576 155.6 576 229.1C576 297.7 533.1 358 496.9 401.9C452.8 455.5 399.6 502 363.1 529.8C350.8 539.2 335.6 543.9 320 543.9C304.4 543.9 289.2 539.2 276.9 529.8C240.4 502 187.2 455.5 143.1 402C106.9 358.1 64 297.7 64 229.1C64 155.6 123.6 96 197.1 96C239.8 96 280 116.5 305 151.1L320 171.8L335 151.1z"/></svg>' +
          '</span>' +
          '<span data-wishlist-icon-on class="hidden h-6 w-6 items-center justify-center">' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="h-6 w-6 fill-current"><path d="M305 151.1L320 171.8L335 151.1C360 116.5 400.2 96 442.9 96C516.4 96 576 155.6 576 229.1L576 231.7C576 343.9 436.1 474.2 363.1 529.9C350.7 539.3 335.5 544 320 544C304.5 544 289.2 539.4 276.9 529.9C203.9 474.2 64 343.9 64 231.7L64 229.1C64 155.6 123.6 96 197.1 96C239.8 96 280 116.5 305 151.1z"/></svg>' +
          '</span>' +
        '</button>' +
      '</div>'
    );
  }

  // ── Image loading — mirrors product-card.liquid's fade-in/fallback swap ──
  // (the non-priority branch; nothing in the theme renders product-card with
  // priority: true today, so there's no "eager" variant to mirror here).

  var IMG_ONERROR_JS =
    "const wrapper = this.closest('[data-image-wrapper]');" +
    "if (this.dataset.fallbackSrc && this.src !== this.dataset.fallbackSrc) { this.src = this.dataset.fallbackSrc; }" +
    "if (wrapper) wrapper.dataset.loaded = 'true';";

  var IMG_ONLOAD_JS =
    "const wrapper = this.closest('[data-image-wrapper]');" +
    "const markLoaded = () => { if (!wrapper) return; const target = this.dataset.src || this.getAttribute('data-src'); if (!target || this.currentSrc.includes(target) || this.src.includes(target)) { wrapper.dataset.loaded = 'true'; } };" +
    "const fallbackTimer = setTimeout(() => { if (!wrapper || wrapper.dataset.loaded === 'true') return; if (this.dataset.fallbackSrc && this.src !== this.dataset.fallbackSrc) { this.src = this.dataset.fallbackSrc; } wrapper.dataset.loaded = 'true'; }, 2000);" +
    "if (!this.dataset._swapped && this.dataset.src && this.src !== this.dataset.src) {" +
    "  this.dataset._swapped = 'true';" +
    "  const real = new Image();" +
    "  real.src = this.dataset.src;" +
    "  real.onload = () => { this.style.opacity = 0; setTimeout(() => { this.src = real.src; this.style.opacity = 1; markLoaded(); clearTimeout(fallbackTimer); }, 150); };" +
    "  real.onerror = () => { if (wrapper) wrapper.dataset.loaded = 'true'; clearTimeout(fallbackTimer); };" +
    "} else {" +
    "  markLoaded();" +
    "  clearTimeout(fallbackTimer);" +
    "}";

  function imageWrapperHtml(hit, fallbackImg, title) {
    var fallbackEsc = esc(fallbackImg);

    if (!hit.image) {
      return (
        '<div class="relative w-full h-[130px] min-h-[180px]" data-image-wrapper data-fallback-src="' + fallbackEsc + '" data-loaded="true">' +
          '<img' +
            ' src="' + fallbackEsc + '"' +
            ' data-fallback-src="' + fallbackEsc + '"' +
            ' alt="' + title + '"' +
            ' width="170" height="170"' +
            ' loading="lazy" decoding="async"' +
            ' class="w-full h-[170px] object-contain"' +
          '>' +
        '</div>'
      );
    }

    return (
      '<div class="relative w-full h-[130px] min-h-[180px]" data-image-wrapper data-fallback-src="' + fallbackEsc + '">' +
        '<span class="image-loader" aria-hidden="true"></span>' +
        '<img' +
          ' src="' + fallbackEsc + '"' +
          ' data-src="' + esc(hit.image) + '"' +
          ' data-fallback-src="' + fallbackEsc + '"' +
          ' alt="' + title + '"' +
          ' width="170" height="170"' +
          ' loading="lazy" decoding="async"' +
          ' class="w-full h-[170px] object-contain transition-transform duration-300 group-hover:scale-105 opacity-100"' +
          ' onerror="' + IMG_ONERROR_JS + '"' +
          ' onload="' + IMG_ONLOAD_JS + '"' +
        '>' +
      '</div>'
    );
  }

  // ── Per-page instance ──────────────────────────────────────────────────────
  // Each section calls create() once with its own fallback image / Judge.me
  // config; vendor/type facet captures are per-instance so two grids on the
  // same page (shouldn't happen, but just in case) don't share state.

  function create(config) {
    config = config || {};
    var fallbackImg        = config.fallbackImg || '';
    var judgemeShopDomain  = config.judgemeShopDomain || '';
    var judgemePublicToken = config.judgemePublicToken || '';
    var judgemeBadgeCache  = {};
    var vendorHandleSet    = new Set();
    var typeHandleSet      = new Set();

    // ── Judge.me review badges ───────────────────────────────────────────────
    // Cards are injected by Algolia/JS rather than Shopify's own product loop, so
    // Judge.me's storefront widget never learns these products exist. Fetch each
    // badge directly from Judge.me's public preview_badge endpoint instead.

    function loadReviewBadges(root) {
      if (!judgemePublicToken) return;

      (root || document).querySelectorAll('.jdgm-preview-badge[data-id]').forEach(function (el) {
        var id = el.getAttribute('data-id');
        if (!id) return;

        if (judgemeBadgeCache[id]) {
          el.innerHTML = judgemeBadgeCache[id];
          return;
        }

        var url = 'https://api.judge.me/api/v1/widgets/preview_badge' +
          '?shop_domain=' + encodeURIComponent(judgemeShopDomain) +
          '&platform=shopify' +
          '&external_id=' + encodeURIComponent(id) +
          '&api_token=' + encodeURIComponent(judgemePublicToken);

        fetch(url)
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (data) {
            if (data && data.badge) {
              judgemeBadgeCache[id] = data.badge;
              el.innerHTML = data.badge;
            }
          })
          .catch(function () {});
      });
    }

    function hitTemplate(hit) {
      var title     = esc(hit.title || '');
      var vendor    = esc(hit.vendor || '');
      var handle    = esc(hit.handle || '');
      var productId = esc(hit.id || '');
      var available = hit.inventory_available !== false && hit.inventory_available !== 0;

      // "From $X" for multi-variant products with a price range — mirrors
      // product.price_varies in product-card.liquid. variants_min_price /
      // variants_max_price come from the Algolia app's default product record.
      var priceVaries =
        hit.variants_min_price != null &&
        hit.variants_max_price != null &&
        Number(hit.variants_min_price) !== Number(hit.variants_max_price);

      var priceBlockHtml;
      if (priceVaries) {
        priceBlockHtml = '<p class="text-gray-700 text-base">From ' + formatPrice(hit.variants_min_price) + '</p>';
      } else {
        var compareAtHtml = '';
        if (hit.compare_at_price && Number(hit.compare_at_price) > Number(hit.price || 0)) {
          var savings = Number(hit.compare_at_price) - Number(hit.price || 0);
          var pct     = Math.round((savings / Number(hit.compare_at_price)) * 100);
          compareAtHtml = '<span class="text-[#d6452b] text-md font-light">~' + pct + '% Off</span>';
        }
        priceBlockHtml =
          '<div class="flex items-baseline justify-center md:justify-start gap-2 mt-3">' +
            '<p class="text-gray-700 text-lg">' + formatPrice(hit.price) + '</p>' +
            compareAtHtml +
          '</div>';
      }

      var starsHtml =
        '<div class="jdgm-widget jdgm-preview-badge review-stars mb-1"' +
          ' data-id="' + productId + '">' +
        '</div>';

      // NOTE: product-card.liquid also gates this on `variant.inventory_management
      // == 'shopify'` (hides the line entirely for untracked variants) — the
      // Algolia record has no equivalent field, so that gate can't be mirrored
      // here without adding inventory_management to the app's index attributes.
      var inventoryHtml = '';
      var invQty = null;
      if (hit.inventory_quantity != null) {
        invQty = Number(hit.inventory_quantity);
      } else if (hit.variants && hit.variants[0] && hit.variants[0].inventory_quantity != null) {
        invQty = Number(hit.variants[0].inventory_quantity);
      }

      if (invQty !== null) {
        if (invQty <= 0) {
          inventoryHtml = '<div class="text-xs italic"><span class="text-red-600">Out of stock</span></div>';
        } else if (invQty <= 5) {
          inventoryHtml = '<div class="text-xs italic"><span class="text-red-600">Only ' + invQty + ' left!</span></div>';
        } else {
          inventoryHtml = '<div class="text-xs italic"><span class="text-green-600">In stock</span></div>';
        }
      } else {
        inventoryHtml = available
          ? '<div class="text-xs italic"><span class="text-green-600">In stock</span></div>'
          : '<div class="text-xs italic"><span class="text-red-600">Out of stock</span></div>';
      }

      return (
        '<div class="relative">' +
          wishlistButtonHtml(hit) +
          '<a href="/products/' + handle + '" class="block relative overflow-hidden">' +
            tagBadgesHtml(hit) +
            imageWrapperHtml(hit, fallbackImg, title) +
          '</a>' +
        '</div>' +
        '<div class="md:p-4 text-center md:text-left">' +
          '<p class="text-xs text-[#00538d] mb-1">' + vendor + '</p>' +
          '<a href="/products/' + handle + '" class="block relative overflow-hidden">' +
            '<h3 class="text-sm font-light text-[#00538d] uppercase">' + title + '</h3>' +
          '</a>' +
          starsHtml +
          priceBlockHtml +
          inventoryHtml +
          '<button' +
            ' type="button"' +
            ' data-product-handle="' + handle + '"' +
            ' data-action="quick-view"' +
            ' data-product-card-quick-view' +
            ' data-inventory-quantity="' + (invQty !== null ? invQty : '') + '"' +
            ' class="hidden absolute left-1/2 top-2/5 -translate-x-1/2 group-hover:flex group-hover:opacity-100 transition-all duration-300 z-20 cursor-pointer"' +
          '>' +
            '<span class="bg-[#00538d] text-white text-sm font-medium px-4 py-2 rounded-sm shadow-sm hover:bg-white hover:border hover:border-gray-500 hover:text-black transition-colors">Quick View</span>' +
          '</button>' +
        '</div>'
      );
    }

    function makeRefinementList(containerId, attribute, opts) {
      opts = opts || {};
      var expanded = false;
      var lastRender = function () {};
      return instantsearch.connectors.connectRefinementList(function render(renderState) {
        lastRender = function () { render(renderState); };
        var container = document.getElementById(containerId);
        if (!container) return;

        var items = opts.excludeValue
          ? renderState.items.filter(function (item) { return item.value !== opts.excludeValue; })
          : renderState.items;

        // Record this facet's values so the Pattern list can dedupe against them
        if (opts.capture) {
          opts.capture.clear();
          items.forEach(function (item) { opts.capture.add(toHandle(item.value)); });
        }

        // Category list: only show items in the allowlist
        if (opts.include) {
          items = items.filter(function (item) { return opts.include.has(toHandle(item.value)); });
        }

        // Pattern list: strip anything that's really a category, vendor, or
        // style collection duplicated from another facet
        if (opts.exclude) {
          items = items.filter(function (item) {
            var handle = toHandle(item.value);
            return !opts.exclude.some(function (set) { return set.has(handle); });
          });
        }

        var refine = renderState.refine;

        // Hide the whole group when there's nothing to pick (e.g. Pattern on a
        // factory page whose products have no pattern collection)
        var group = container.closest('.facet-group');
        if (group) group.classList.toggle('hidden', !items.length);

        if (!items.length) {
          container.innerHTML = '<span class="text-xs text-gray-400 italic">No options</span>';
          return;
        }

        // Long lists: show the top `showTop` (refined values always sort first),
        // with a toggle for the rest
        var total = items.length;
        var collapsible = opts.showTop && total > opts.showTop;
        if (collapsible && !expanded) items = items.slice(0, opts.showTop);

        container.innerHTML = items.map(function (item) {
          var activeClass = item.isRefined ? 'text-[#00538d] font-semibold' : 'text-gray-700';
          return (
            '<a href="#" data-ais-refine="' + esc(item.value) + '"' +
              ' class="flex items-center justify-between text-sm cursor-pointer hover:text-[#00538d] hover:font-medium ' + activeClass + '">' +
              (opts.collectionTitles
                ? '<span data-collection-label="' + esc(item.value) + '">' + esc(collectionLabel(item.value)) + '</span>'
                : '<span>' + esc(opts.formatLabel ? opts.formatLabel(item.label) : item.label) + '</span>') +
              '<span class="text-gray-500">(' + item.count + ')</span>' +
            '</a>'
          );
        }).join('') + (collapsible
          ? '<button type="button" data-ais-show-more class="mt-1 text-sm text-[#00538d] underline cursor-pointer">' +
              (expanded ? 'Show less' : 'Show all (' + total + ')') +
            '</button>'
          : '');

        container.querySelectorAll('a[data-ais-refine]').forEach(function (link) {
          link.addEventListener('click', function (e) {
            e.preventDefault();
            refine(link.dataset.aisRefine);
          });
        });

        var toggle = container.querySelector('[data-ais-show-more]');
        if (toggle) {
          toggle.addEventListener('click', function () {
            expanded = !expanded;
            lastRender();
          });
        }
      })({
        attribute: attribute,
        operator: 'or',
        // include/exclude lists filter AFTER Algolia returns values, so ask for
        // all of them (Algolia's max) — otherwise real values past the top 30
        // never arrive (e.g. Georgia Blue ranks #55 of 197 on Bakeware)
        limit: opts.limit || (opts.include || opts.exclude ? 1000 : 30),
        sortBy: opts.sortBy || ['isRefined', 'count:desc', 'name:asc'],
      });
    }

    function makePriceMenu(containerId) {
      return instantsearch.connectors.connectNumericMenu(function (renderState) {
        var container = document.getElementById(containerId);
        if (!container) return;

        container.innerHTML = renderState.items.map(function (item) {
          var activeClass = item.isRefined ? 'text-[#00538d] font-semibold' : 'text-gray-700';
          return (
            '<a href="' + item.url + '"' +
              ' data-ais-numeric="' + esc(item.value) + '"' +
              ' class="flex items-center justify-between text-sm cursor-pointer hover:text-[#00538d] hover:font-medium ' + activeClass + '">' +
              '<span>' + esc(item.label) + '</span>' +
            '</a>'
          );
        }).join('');

        container.querySelectorAll('a[data-ais-numeric]').forEach(function (link) {
          link.addEventListener('click', function (e) {
            e.preventDefault();
            renderState.refine(link.dataset.aisNumeric);
          });
        });
      })({
        attribute: 'price',
        items: [
          { label: '$10 to $50',       start: 10,  end: 50 },
          { label: '$50 to $100',      start: 50,  end: 100 },
          { label: '$100 to $200',     start: 100, end: 200 },
          { label: '$200 to $300',     start: 200, end: 300 },
          { label: '$300 to $400',     start: 300, end: 400 },
          { label: '$400 to $500',     start: 400, end: 500 },
          { label: 'More than $550',   start: 550 },
        ],
      });
    }

    // The sidebar facet set shared by the collection and search pages — define
    // filters here, not per-section. Order matters: type/vendor must render
    // before Pattern so their captured handle sets are filled when Pattern dedupes.
    // opts.excludeCollection: handle to hide from Pattern (the current collection).
    // opts.availability: also add an In Stock / Out of Stock list (#ais-availability-list).
    function makeStandardFacets(opts) {
      opts = opts || {};
      loadCollectionTitles();
      var widgets = [
        makeRefinementList('ais-color-list', 'meta.custom.product_colors.fields.label'),
        makeRefinementList('ais-type-list', 'product_type', { capture: typeHandleSet }),
        makeRefinementList('ais-vendor-list', 'vendor', { capture: vendorHandleSet }),

        // Category — merchandising/nav collections (Best Sellers, Shop All, Kitchen
        // Accessories, ...) split out of the 'collections' attribute
        makeRefinementList('ais-category-list', 'collections', { include: CATEGORY_HANDLES, collectionTitles: true }),

        // Pattern — 'collections' includes every collection a product belongs to,
        // not just its pattern, so strip category/vendor/style collections
        makeRefinementList('ais-pattern-list', 'collections', {
          excludeValue: opts.excludeCollection,
          exclude: [CATEGORY_HANDLES, vendorHandleSet, typeHandleSet, PATTERN_ONLY_EXCLUDE],
          collectionTitles: true,
          showTop: 15,
        }),

        makePriceMenu('ais-price-list'),

        // Capacity — Product Capacity metafield, indexed as "12.0" / "6.75"
        makeRefinementList('ais-capacity-list', 'meta.custom.product_capacity', {
          formatLabel: function (value) { return Number(value) + ' oz'; },
          sortBy: function (a, b) { return Number(a.name) - Number(b.name); },
          limit: 100,
          showTop: 10,
        }),
      ];

      if (opts.availability) {
        widgets.push(makeRefinementList('ais-availability-list', 'inventory_available', {
          formatLabel: function (value) { return value === 'true' ? 'In Stock' : 'Out of Stock'; },
        }));
      }

      return widgets;
    }

    function makeHits(opts) {
      opts = opts || {};

      // Latest rendered hits, so the delegated click listener below can map a
      // clicked card back to its hit (and its __queryID / __position).
      var clickState = { hits: [], sendEvent: null, index: '' };

      // Algolia Insights click event + remember the click so an add to cart
      // can be credited to this search (snippets/algolia-insights.liquid).
      // Only the product link and Quick View count, not the wishlist heart.
      function bindClickTracking(hitsContainer) {
        if (hitsContainer.dataset.insightsBound) return;
        hitsContainer.dataset.insightsBound = '1';
        hitsContainer.addEventListener('click', function (event) {
          var trigger = event.target.closest('a[href^="/products/"], [data-action="quick-view"]');
          if (!trigger) return;
          var card = trigger.closest('[data-hit-index]');
          if (!card || !hitsContainer.contains(card)) return;
          var hit = clickState.hits[Number(card.getAttribute('data-hit-index'))];
          if (!hit) return;
          try {
            if (clickState.sendEvent) clickState.sendEvent('click', hit, 'Product Clicked');
            if (window.BlueRoseInsights) {
              window.BlueRoseInsights.rememberClick(hit.handle, clickState.index, hit.__queryID, hit.objectID);
            }
          } catch (e) { /* tracking must never block navigation */ }
        });
      }

      return instantsearch.connectors.connectHits(function (renderState) {
        var hits    = renderState.hits;
        var results = renderState.results;
        var nbHits  = results ? results.nbHits : 0;

        var hitsContainer = document.getElementById('ais-hits');
        var paginationEl  = document.getElementById('ais-pagination');
        var loadingText   = opts.loadingTextId ? document.getElementById(opts.loadingTextId) : null;

        if (!hitsContainer) return;
        if (loadingText) loadingText.classList.add('hidden');
        bindClickTracking(hitsContainer);

        if (!hits.length) {
          clickState.hits = [];
          var emptyHtml = typeof opts.emptyHtml === 'function' ? opts.emptyHtml() : opts.emptyHtml;
          hitsContainer.innerHTML = emptyHtml ||
            '<li class="col-span-full text-center py-12 text-gray-500">No products match your current filters.</li>';
          if (paginationEl) paginationEl.classList.add('hidden');
          return;
        }

        // The Algolia app indexes one row per variant, not per product, and
        // this index's attributeForDistinct (meta.custom.pattern) isn't set on
        // every product and isn't a per-product key anyway — so Algolia's own
        // `distinct` would either miss these dupes or wrongly collapse unrelated
        // products that share a pattern. Dedupe client-side by product id instead.
        var seenIds = new Set();
        var dedupedHits = hits.filter(function (hit) {
          var id = hit.id;
          if (id == null || !seenIds.has(id)) {
            if (id != null) seenIds.add(id);
            return true;
          }
          return false;
        });

        clickState.hits      = dedupedHits;
        clickState.sendEvent = renderState.sendEvent;
        clickState.index     = results ? results.index : '';

        hitsContainer.innerHTML = dedupedHits.map(function (hit, i) {
          return (
            '<li class="group relative bg-white rounded-sm overflow-hidden duration-300 py-8 border-b border-dotted border-gray-300 p-2 md:p-4"' +
              ' data-hit-index="' + i + '"' +
              ' data-available="' + (hit.available !== false) + '">' +
              hitTemplate(hit) +
            '</li>'
          );
        }).join('');

        if (paginationEl) {
          paginationEl.dataset.nbHits = nbHits;
          paginationEl.dataset.hpp    = results ? results.hitsPerPage : 24;
          paginationEl.classList.toggle('hidden', !results || results.nbPages <= 1);
        }

        loadReviewBadges(hitsContainer);
        if (window.BlueRoseWishlist) window.BlueRoseWishlist.syncWishlistButtons(hitsContainer);
      })({});
    }

    function makePagination() {
      return instantsearch.connectors.connectPagination(function (renderState) {
        var container = document.getElementById('ais-pagination');
        if (!container || container.classList.contains('hidden')) return;

        var current  = renderState.currentRefinement;
        var nbPages  = renderState.nbPages;
        var refine   = renderState.refine;
        var nbHits   = Number(container.dataset.nbHits || 0);
        var hpp      = Number(container.dataset.hpp || 24);
        var start    = current * hpp + 1;
        var end      = Math.min((current + 1) * hpp, nbHits);

        var pages   = [];
        var padding = 2;
        var lo = Math.max(0, current - padding);
        var hi = Math.min(nbPages - 1, current + padding);
        for (var p = lo; p <= hi; p++) pages.push(p);

        var btnBase   = 'px-3 py-1 border rounded text-[#00538d] hover:bg-gray-100';
        var btnActive = 'px-3 py-1 border rounded bg-[#00538d] text-white';

        var pageLinks = pages.map(function (p) {
          if (p === current) return '<span class="' + btnActive + '">' + (p + 1) + '</span>';
          return '<a href="#" data-page="' + p + '" class="' + btnBase + '">' + (p + 1) + '</a>';
        }).join('');

        container.innerHTML =
          '<div class="text-left text-xs text-gray-600 pb-4 md:pb-0">' +
            'Showing ' + start + ' – ' + end + ' of ' + nbHits + ' items' +
          '</div>' +
          '<nav role="navigation" class="flex justify-center items-center gap-2 text-sm font-medium">' +
            (current > 0 ? '<a href="#" data-page="' + (current - 1) + '" class="' + btnBase + '">&larr;</a>' : '') +
            pageLinks +
            (current < nbPages - 1 ? '<a href="#" data-page="' + (current + 1) + '" class="' + btnBase + '">&rarr;</a>' : '') +
          '</nav>';

        container.querySelectorAll('a[data-page]').forEach(function (link) {
          link.addEventListener('click', function (e) {
            e.preventDefault();
            refine(Number(link.dataset.page));
            window.scrollTo({ top: 0, behavior: 'smooth' });
          });
        });
      })({ padding: 2 });
    }

    function watchResetButton(search) {
      var btn = document.getElementById('ais-reset-btn');
      if (!btn) return;

      btn.addEventListener('click', function () {
        search.helper.clearRefinements().search();
      });

      search.on('render', function () {
        var helper = search.helper;
        if (!helper) return;
        var state = helper.state;
        var hasDisjunctive = Object.values(state.disjunctiveFacetsRefinements || {}).some(function (v) { return v.length > 0; });
        var hasNumeric     = Object.values(state.numericRefinements || {}).some(function (ranges) {
          return Object.values(ranges).some(function (ops) { return ops.length > 0; });
        });
        btn.classList.toggle('hidden', !hasDisjunctive && !hasNumeric);
      });
    }

    // Render owns the active .ais-view-option classes; click only calls refine
    function makeHitsPerPage(items) {
      var latestHppRefine = null;
      return instantsearch.connectors.connectHitsPerPage(function (renderState, isFirstRender) {
        latestHppRefine = renderState.refine;
        renderState.items.forEach(function (item) {
          document.querySelectorAll('.ais-view-option').forEach(function (btn) {
            if (Number(btn.dataset.hpp) === item.value) {
              btn.classList.toggle('text-[#00538d]', item.isRefined);
              btn.classList.toggle('font-semibold', item.isRefined);
              btn.classList.toggle('text-gray-600', !item.isRefined);
            }
          });
        });
        if (isFirstRender) {
          document.querySelectorAll('.ais-view-option').forEach(function (btn) {
            btn.addEventListener('click', function () {
              if (latestHppRefine) latestHppRefine(Number(btn.dataset.hpp));
            });
          });
        }
      })({ items: items });
    }

    // Widget lives here for IS.js routing/state management; onRefine hands the
    // refine fn back to the caller so the #ais-sort-select listener (added
    // after search.start()) can call it without an early-return race during init.
    function makeSortBy(items, onRefine) {
      return instantsearch.connectors.connectSortBy(function (renderState) {
        if (onRefine) onRefine(renderState.refine);
        var sel = document.getElementById('ais-sort-select');
        if (sel && sel.value !== renderState.currentRefinement) {
          sel.value = renderState.currentRefinement;
        }
      })({ items: items });
    }

    return {
      hitTemplate: hitTemplate,
      loadReviewBadges: loadReviewBadges,
      makeRefinementList: makeRefinementList,
      makePriceMenu: makePriceMenu,
      makeStandardFacets: makeStandardFacets,
      makeHits: makeHits,
      makePagination: makePagination,
      watchResetButton: watchResetButton,
      makeHitsPerPage: makeHitsPerPage,
      makeSortBy: makeSortBy,
      vendorHandleSet: vendorHandleSet,
      typeHandleSet: typeHandleSet,
    };
  }

  // ── Mobile filter drawer ────────────────────────────────────────────────────
  // Identical on both pages (data-search-filters-* attributes only), and only
  // one of the two sections is ever on a page at once, so one shared bind flag
  // is enough.

  function bindMobileFilterDrawer() {
    if (window.__blueRoseFiltersBound) return;
    window.__blueRoseFiltersBound = true;

    var getEl = function () {
      return {
        toggle:  document.querySelector('[data-search-filters-toggle]'),
        drawer:  document.querySelector('[data-search-filters-drawer]'),
        overlay: document.querySelector('[data-search-filters-overlay]'),
      };
    };

    var desktopQuery      = window.matchMedia('(min-width: 768px)');
    var focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    var lastTrigger       = null;

    var getFocusable = function (container) {
      return Array.from(container.querySelectorAll(focusableSelector)).filter(function (el) {
        if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') return false;
        return el.offsetParent !== null || el === document.activeElement;
      });
    };

    var syncA11y = function (isOpen) {
      var el = getEl();
      if (!el.drawer) return;
      var isDesktop = desktopQuery.matches;
      el.drawer.toggleAttribute('inert', !isDesktop && !isOpen);
      el.drawer.setAttribute('aria-hidden', isDesktop || isOpen ? 'false' : 'true');
      if (el.toggle) el.toggle.setAttribute('aria-expanded', String(!isDesktop && isOpen));
    };

    var openDrawer = function () {
      var el = getEl();
      if (!el.toggle || !el.drawer || !el.overlay) return;
      lastTrigger = el.toggle;
      el.drawer.classList.remove('-translate-x-full');
      el.overlay.classList.remove('opacity-0', 'pointer-events-none');
      syncA11y(true);
      document.body.classList.add('overflow-hidden');
      var first = getFocusable(el.drawer)[0];
      if (first) first.focus({ preventScroll: true });
    };

    var closeDrawer = function (opts) {
      var restore = !opts || opts.restoreFocus !== false;
      var el = getEl();
      document.body.classList.remove('overflow-hidden');
      if (!el.toggle || !el.drawer || !el.overlay) return;
      el.drawer.classList.add('-translate-x-full');
      el.overlay.classList.add('opacity-0', 'pointer-events-none');
      if (restore && lastTrigger && lastTrigger.isConnected) lastTrigger.focus({ preventScroll: true });
      syncA11y(false);
    };

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-search-filters-toggle]')) { e.preventDefault(); openDrawer(); return; }
      if (e.target.closest('[data-search-filters-close]') || e.target.closest('[data-search-filters-overlay]')) {
        e.preventDefault();
        closeDrawer();
      }
    });

    document.addEventListener('keydown', function (e) {
      var el = getEl();
      if (!el.drawer || el.drawer.hasAttribute('inert') || desktopQuery.matches) return;
      if (e.key === 'Escape') { closeDrawer(); return; }
      if (e.key !== 'Tab') return;
      var focusable = getFocusable(el.drawer);
      if (!focusable.length) return;
      var first = focusable[0];
      var last  = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first)       { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last)  { e.preventDefault(); first.focus(); }
    });

    var handleDesktopChange = function (e) {
      syncA11y(false);
      if (e.matches) closeDrawer({ restoreFocus: false });
    };
    if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', handleDesktopChange);
    else desktopQuery.addListener(handleDesktopChange);
    syncA11y(false);
  }

  // Attach Algolia Insights (views + clickAnalytics) to an InstantSearch
  // instance. Call before search.start(). No-op if the snippet isn't loaded.
  function useInsights(search) {
    try {
      var middleware = window.BlueRoseInsights && window.BlueRoseInsights.middleware();
      if (middleware) search.use(middleware);
    } catch (e) { /* search must work even if insights does not */ }
  }

  return {
    esc: esc,
    formatPrice: formatPrice,
    useInsights: useInsights,
    tagBadgesHtml: tagBadgesHtml,
    toHandle: toHandle,
    CATEGORY_HANDLES: CATEGORY_HANDLES,
    PATTERN_ONLY_EXCLUDE: PATTERN_ONLY_EXCLUDE,
    create: create,
    bindMobileFilterDrawer: bindMobileFilterDrawer,
  };
})();
